import { useFormik } from 'formik';
import { useContext, useState, useCallback } from 'react';
import * as Yup from 'yup';
import Button from '../../../components/bootstrap/Button';
import Card, {
	CardBody,
	CardFooter,
	CardHeader,
	CardLabel,
	CardTitle,
} from '../../../components/bootstrap/Card';
import Spinner from '../../../components/bootstrap/Spinner';
import FormGroup from '../../../components/bootstrap/forms/FormGroup';
import Input from '../../../components/bootstrap/forms/Input';
import Select from '../../../components/bootstrap/forms/Select';
import AuthContext from '../../../contexts/authContext';
import StatusPopup from '../../../components/PlanningCalendar/StatusPopup';
import ErrorPopup from '../../../components/extras/ErrorPopup';
import SearchAutoComplete from '../../../components/customComponents/SearchAutoComplete';
import { AutoComplete, getPlaceDetails } from '../../../googleApi/placeAutoComplete';
import { Modal } from 'react-bootstrap';

const validationSchema = Yup.object({
	nom: Yup.string().required('Le nom est obligatoire'),
	telephones: Yup.array()
		.of(Yup.string().matches(/^[0-9+\s-]*$/, 'Numéro invalide'))
		.min(1, 'Au moins un numéro requis'),
	emails: Yup.array().of(Yup.string().email('Email invalide')),
	type: Yup.string()
		.oneOf(['particulier', 'societe'], 'Type invalide')
		.required('Le type est obligatoire'),
	// statut: Yup.string().oneOf(
	// 	[
	// 		'prospect',
	// 		'hors_sujet',
	// 		'interesse',
	// 		'devis_envoye',
	// 		'client_actif',
	// 		'fidelisation',
	// 		'perdu',
	// 	],
	// 	'Statut invalide',
	// ),
	matriculeFiscale: Yup.string()
		.test(
			'matricule-required',
			'Matricule fiscale est obligatoire pour les sociétés',
			function (value) {
				const { type } = this.parent;
				if (type === 'societe') {
					return value && value.trim().length > 0;
				}
				return true;
			}
		)
		.test(
			'matricule-format',
			'Format de matricule fiscale invalide',
			function (value) {
				const { type } = this.parent;
				if (type === 'societe' && value) {
					return value.trim().length > 0;
				}
				return true;
			}
		),
	typeOccupation: Yup.string().oneOf(['proprietaire', 'locataire', '']),
	interphone: Yup.string(),
	numeroAppartement: Yup.string(),
	codeAcces: Yup.string(),
	numeroProprietaire: Yup.string().test(
		'proprietaire-phone-required',
		'Numéro du propriétaire requis pour les locataires',
		function (value) {
			const { typeOccupation } = this.parent;
			if (typeOccupation === 'locataire') {
				return value && value.trim().length > 0;
			}
			return true;
		},
	),
	billingAddresses: Yup.array().of(
		Yup.object().shape({
			formatted: Yup.string(),
			street: Yup.string(),
			city: Yup.string(),
			zip: Yup.string(),
			state: Yup.string(),
			country: Yup.string(),
			location: Yup.object().shape({
				type: Yup.string().oneOf(['Point']),
				coordinates: Yup.array()
					.of(Yup.number())
					.length(2, 'Les coordonnées doivent contenir [longitude, latitude]')
					.test('valid-coordinates', 'Coordonnées invalides', function (value) {
						if (!value || value.length !== 2) return true;
						const [lng, lat] = value;
						return lng >= -180 && lng <= 180 && lat >= -90 && lat <= 90;
					}),
			}),
		}),
	),
	shippingAddresses: Yup.array().of(
		Yup.object().shape({
			formatted: Yup.string(),
			street: Yup.string(),
			city: Yup.string(),
			zip: Yup.string(),
			state: Yup.string(),
			country: Yup.string(),

			location: Yup.object().shape({
				type: Yup.string().oneOf(['Point']),
				coordinates: Yup.array()
					.of(Yup.number())
					.length(2, 'Les coordonnées doivent contenir [longitude, latitude]')
					.test('valid-coordinates', 'Coordonnées invalides', function (value) {
						if (!value || value.length !== 2) return true;
						const [lng, lat] = value;
						return lng >= -180 && lng <= 180 && lat >= -90 && lat <= 90;
					}),
			}),
		}),
	),
});

const ManageClient = ({ data, setIsOpen, createClient, isNewClient = false }) => {
	const [spinner, setSpinner] = useState(false);
	const [success, setSuccess] = useState('');
	const [error, setError] = useState('');
	const [billingSearchResults, setBillingSearchResults] = useState({});
	const [shippingSearchResults, setShippingSearchResults] = useState({});
	const { userData } = useContext(AuthContext);

	const initializeAddress = (addr) => {
		if (!addr || Object.keys(addr).length === 0) {
			return {
				formatted: '',
				street: '',
				city: '',
				zip: '',
				state: '',
				country: '',
				map_url: '', // Added here
				location: {
					type: 'Point',
					coordinates: [0, 0],
				},
			};
		}

		return {
			formatted: addr?.formatted || '',
			street: addr?.street || '',
			city: addr?.city || '',
			zip: addr?.zip || '',
			state: addr?.state || '',
			country: addr?.country || '',
			map_url: addr?.map_url || '', // Added here
			location: {
				type: 'Point',
				coordinates:
					Array.isArray(addr?.location?.coordinates) &&
						addr.location.coordinates.length === 2
						? [...addr.location.coordinates]
						: [0, 0],
			},
		};
	};

	const handleClose = () => setIsOpen(false);

	const formik = useFormik({
		initialValues: {
			nom: data?.nom || '',
			nomFacturation: data?.nomFacturation || '',
			telephones: Array.isArray(data?.telephones) ? data.telephones : [''],
			telephones_labels: Array.isArray(data?.telephones_labels) ? data.telephones_labels : [''],
			emails: Array.isArray(data?.emails) ? data.emails : [''],
			type: data?.type || 'particulier',
			statut: data?.statut || 'prospect',
			source: data?.source || 'appel',
			matriculeFiscale: data?.matriculeFiscale || '',
			typeOccupation: data?.typeOccupation || 'proprietaire',
			interphone: data?.interphone || '',
			numeroAppartement: data?.numeroAppartement || '',
			numeroProprietaire: data?.numeroProprietaire || '',
			codeAcces: data?.codeAcces || '',
			billingAddresses: Array.isArray(data?.billingAddresses)
				? data.billingAddresses.map((addr) => initializeAddress(addr))
				: data?.billingAddress
					? [initializeAddress(data.billingAddress)]
					: [initializeAddress()],
			shippingAddresses: Array.isArray(data?.shippingAddresses)
				? data.shippingAddresses.map((addr) => initializeAddress(addr))
				: data?.shippingAddress
					? [initializeAddress(data.shippingAddress)]
					: [initializeAddress()],
		},
		validationSchema,
		onSubmit: async (values) => {
			setSpinner(true);
			setSuccess('');
			setError('');
			try {
				const cleanAddresses = (addresses) =>
					addresses
						.map((addr) => {
							const cleaned = { ...addr };

							Object.keys(cleaned).forEach((key) => {
								if (
									key !== 'location' &&
									(cleaned[key] === '' || cleaned[key] === undefined)
								) {
									delete cleaned[key];
								}
							});

							if (cleaned.location) {
								if (
									!cleaned.location.coordinates ||
									cleaned.location.coordinates.length !== 2 ||
									(cleaned.location.coordinates[0] === 0 &&
										cleaned.location.coordinates[1] === 0)
								) {
									delete cleaned.location;
								} else {
									cleaned.location.type = 'Point';
								}
							}

							const hasContent = Object.keys(cleaned).some(
								(key) =>
									key !== 'location' &&
									cleaned[key] !== '' &&
									cleaned[key] !== undefined,
							);

							return hasContent || cleaned.location ? cleaned : null;
						})
						.filter((addr) => addr !== null);

				const submitData = {
					...values,
					typeOccupation: values.typeOccupation || "proprietaire",
					interphone: values.interphone?.trim() || undefined,
					numeroAppartement: values.numeroAppartement?.trim() || undefined,
					codeAcces: values.codeAcces?.trim() || undefined,
					numeroProprietaire: values.numeroProprietaire?.trim() || undefined,
					matriculeFiscale: values.type === 'societe' && values.matriculeFiscale
						? values.matriculeFiscale.toUpperCase().trim()
						: undefined,
					billingAddresses: cleanAddresses(values.billingAddresses),
					shippingAddresses: cleanAddresses(values.shippingAddresses),
				};



				const res = await createClient(submitData);

				if (res.success) {
					setSuccess(
						isNewClient ? 'Client créé avec succès.' : 'Client modifié avec succès.',
					);
					setError('');
					setTimeout(() => setIsOpen(false), 1000);
				} else {
					setError(res.error || 'Une erreur est survenue.');
					setSuccess('');
				}
			} catch (err) {
				if (err?.code !== 'ERR_CANCELED') {
					setError(
						err?.message ||
						`Erreur lors de ${isNewClient ? 'la création' : 'la modification'}`,
					);
					setSuccess('');
				}
			} finally {
				setSpinner(false);
			}
		},
	});

	const handleArrayFieldChange = (type, index, value) => {
		const newArray = [...formik.values[type]];
		newArray[index] = value;
		formik.setFieldValue(type, newArray);
	};

	const addField = (type) => {
		// 1. Specific logic for telephones + labels
		if (type === 'telephones') {
			const currentPhones = formik.values.telephones;
			// Optional: Only add if the last phone entry isn't empty
			if (currentPhones[currentPhones.length - 1] !== '') {
				formik.setFieldValue('telephones', [...currentPhones, '']);
				formik.setFieldValue('telephones_labels', [...(formik.values.telephones_labels || []), '']);
			}
			return;
		}

		// 2. Generic logic for other array fields (emails, etc.)
		const currentArray = formik.values[type];
		if (currentArray[currentArray.length - 1] !== '') {
			formik.setFieldValue(type, [...currentArray, '']);
		}
	};

	const removeField = (type, idx) => {
		// 1. Specific logic for telephones + labels
		if (type === 'telephones') {
			const newPhones = [...formik.values.telephones];
			const newLabels = [...(formik.values.telephones_labels || [])];

			if (newPhones.length > 1) {
				newPhones.splice(idx, 1);
				newLabels.splice(idx, 1);
				formik.setFieldValue('telephones', newPhones);
				formik.setFieldValue('telephones_labels', newLabels);
			}
			return;
		}

		// 2. Generic logic for other array fields
		const newArray = [...formik.values[type]];
		if (newArray.length > 1) {
			newArray.splice(idx, 1);
			formik.setFieldValue(type, newArray);
		}
	};

	const handleAddressFieldChange = useCallback(
		(addressType, addressIndex, field, value) => {
			const newAddresses = formik.values[addressType].map((address, idx) =>
				idx === addressIndex ? { ...address, [field]: value } : address,
			);
			formik.setFieldValue(addressType, newAddresses);
		},
		[formik],
	);

	const handleLocationChange = useCallback(
		(addressType, addressIndex, coordIndex, value) => {
			const newAddresses = formik.values[addressType].map((address, idx) => {
				if (idx === addressIndex) {
					const newCoordinates = [...(address.location?.coordinates || [0, 0])];
					newCoordinates[coordIndex] = parseFloat(value) || 0;

					return {
						...address,
						location: {
							type: 'Point',
							coordinates: newCoordinates,
						},
					};
				}
				return address;
			});
			formik.setFieldValue(addressType, newAddresses);
		},
		[formik],
	);

	const addAddress = (type) => {
		const lastAddress = formik.values?.[type][formik.values[type].length - 1];
		console.log("vlastAddress", lastAddress)
		const isEmpty = !lastAddress || Object.values(lastAddress).every((val) =>
			typeof val === 'string'
				? val === ''
				: typeof val === 'object'
					? JSON.stringify(val) === JSON.stringify({ type: 'Point', coordinates: [0, 0] })
					: true,
		);

		if (!isEmpty) {
			formik.setFieldValue(type, [...formik.values[type], initializeAddress()]);
		} else {
			formik.setFieldValue(type, [initializeAddress()]);
		}
	};

	const removeAddress = (type, idx) => {
		const newArray = [...formik.values[type]];
		if (newArray.length > 1) {
			newArray.splice(idx, 1);
			formik.setFieldValue(type, newArray);
		}
	};

	// ----------------- Autocomplete Handlers -----------------
	const handleBillingSearch = (e, idx) => {
		const input = e.target.value;
		handleAddressFieldChange('billingAddresses', idx, 'formatted', input);

		if (input.length > 2) {
			AutoComplete(input, (result) => {
				const predictions =
					result?.predictions?.map((p) => ({
						id: p.place_id,
						label: p.description,
					})) || [];
				setBillingSearchResults((prev) => ({ ...prev, [idx]: predictions }));
			});
		} else {
			setBillingSearchResults((prev) => ({ ...prev, [idx]: [] }));
		}
	};

	const handleShippingSearch = (e, idx) => {
		const input = e.target.value;
		handleAddressFieldChange('shippingAddresses', idx, 'formatted', input);

		if (input.length > 2) {
			AutoComplete(input, (result) => {
				const predictions =
					result?.predictions?.map((p) => ({
						id: p.place_id,
						label: p.description,
					})) || [];
				setShippingSearchResults((prev) => ({ ...prev, [idx]: predictions }));
			});
		} else {
			setShippingSearchResults((prev) => ({ ...prev, [idx]: [] }));
		}
	};

	const fillAddressFromPlace = (place, type, idx) => {
		if (!place) return;

		let street = '';
		let city = '';
		let zip = '';
		let state = '';
		let country = '';
		let map_url = '';

		// Get the map URL from the place object directly
		map_url = place.url || '';

		place.address_components?.forEach((component) => {
			const types = component.types || [];

			if (types.includes('street_number')) {
				street = component.long_name + (street ? ' ' + street : '');
			}
			if (types.includes('route')) {
				street += (street ? ' ' : '') + component.long_name;
			}
			if (types.includes('locality')) {
				city = component.long_name;
			}
			if (types.includes('postal_town') && !city) {
				city = component.long_name;
			}
			if (types.includes('administrative_area_level_1')) {
				state = component.short_name || component.long_name;
			}
			if (types.includes('country')) {
				country = component.long_name;
			}
			if (types.includes('postal_code')) {
				zip = component.long_name;
			}
		});

		// fallback if no street found (e.g. city search)
		if (!street && place.name && place.types?.includes('locality')) {
			street = place.name;
		}

		const formatted = place.formatted_address || '';

		// Get coordinates from geometry
		const lat = place.geometry?.location?.lat ?? 0;
		const lng = place.geometry?.location?.lng ?? 0;

		// Create the complete address object with coordinates
		const address = {
			street,
			city,
			zip,
			state,
			country,
			map_url,
			formatted,
			location: {
				type: 'Point',
				coordinates: [lng, lat], // Note: MongoDB uses [longitude, latitude]
			},
		};

		// Update formik - use setFieldValue with the specific index
		formik.setFieldValue(`${type}[${idx}]`, address);

		// Also clear the search results for this index
		if (type === 'billingAddresses') {
			setBillingSearchResults((prev) => ({ ...prev, [idx]: [] }));
		} else if (type === 'shippingAddresses') {
			setShippingSearchResults((prev) => ({ ...prev, [idx]: [] }));
		}
	};

	const handleSelectBilling = (item, idx) => {
		getPlaceDetails(item.id, (result) => {
			if (result?.result) {
				fillAddressFromPlace(result.result, 'billingAddresses', idx);
			}
		});
		// Clear the search results for this specific index
		setBillingSearchResults((prev) => ({ ...prev, [idx]: [] }));
	};

	const handleSelectShipping = (item, idx) => {
		getPlaceDetails(item.id, (result) => {
			if (result?.result) {
				fillAddressFromPlace(result.result, 'shippingAddresses', idx);
			}
		});
		// Clear the search results for this specific index
		setShippingSearchResults((prev) => ({ ...prev, [idx]: [] }));
	};


	// ----------------- UI -----------------
	return (
		<>
			<Modal onHide={handleClose} show={true} size='xl'>
				<Modal.Header className='d-flex justify-content-between align-items-center'>
					<CardTitle className='h5 mb-0'>
						{isNewClient ? 'Nouveau Client' : 'Modifier Client'}
					</CardTitle>
					<Button color='dark' icon='Close' onClick={handleClose} className='btn-sm' />
				</Modal.Header>

				<form onSubmit={formik.handleSubmit}>
					<Modal.Body className='overflow-auto'>
						<Card shadow='none' className='border-0'>
							<CardBody className='pt-4'>
								<div className='row g-4'>
									{/* Nom */}
									<FormGroup
										className='col-12 col-md-6'
										id='nom'
										label='Nom complet'
									>
										<Input
											name='nom'
											value={formik.values.nom}
											onChange={formik.handleChange}
											onBlur={formik.handleBlur}
											type='text'
											placeholder='Nom du client'
										/>
										{formik.touched.nom && formik.errors.nom && (
											<small className='text-danger'>
												{formik.errors.nom}
											</small>
										)}
									</FormGroup>

									{/* Type */}
									<FormGroup className='col-12 col-md-3' label='Type'>
										<Select
											name='type'
											value={formik.values.type}
											onChange={formik.handleChange}>
											<option value='particulier'>Particulier</option>
											<option value='societe'>Société</option>
										</Select>
									</FormGroup>
									<FormGroup
										className='col-12 col-md-6'
										id='nom'
										label='Nom Facturation'
									>
										<Input
											name='nomFacturation'
											value={formik.values.nomFacturation}
											onChange={formik.handleChange}
											onBlur={formik.handleBlur}
											type='text'

										/>
										{formik.touched.nomFacturation && formik.errors.nomFacturation && (
											<small className='text-danger'>
												{formik.errors.nomFacturation}
											</small>
										)}
									</FormGroup>

									{/* Statut */}
									{/* <FormGroup className='col-12 col-md-3' label='Statut'>
										<Select
											name='statut'
											value={formik.values.statut}
											onChange={formik.handleChange}>
											<option value='prospect'>Prospect</option>
											<option value='hors_sujet'>Hors sujet</option>
											<option value='interesse'>Intéressé</option>
											<option value='devis_envoye'>Devis envoyé</option>
											<option value='client_actif'>Client actif</option>
											<option value='fidelisation'>Fidélisation</option>
											<option value='perdu'>Perdu</option>
										</Select>
									</FormGroup> */}

									<FormGroup className='col-12 col-md-3' label='Source'>
										<Select
											name='source'
											value={formik.values.source}
											onChange={formik.handleChange}>
											<option value='appel'>Appel</option>
											<option value='mail'>Mail</option>
											<option value='whatsapp'>WhatsApp</option>
										</Select>
									</FormGroup>

									{/* Matricule Fiscale - Conditionnel pour les sociétés */}
									{formik.values.type === 'societe' && (
										<FormGroup
											className='col-12 col-md-6'
											id='matriculeFiscale'
											label='Matricule Fiscale'
											isFloating
										>
											<Input
												name='matriculeFiscale'
												value={formik.values.matriculeFiscale || ''}
												onChange={(e) => {
													const value = e.target.value.toUpperCase();
													formik.setFieldValue('matriculeFiscale', value);
												}}
												onBlur={formik.handleBlur}
												type='text'
												placeholder='Matricule Fiscale'
												className='text-uppercase'
											/>
											{formik.touched.matriculeFiscale && formik.errors.matriculeFiscale && (
												<small className='text-danger'>{formik.errors.matriculeFiscale}</small>
											)}
										</FormGroup>
									)}

									{/* NOUVEAUX CHAMPS D'OCCUPATION */}
									<div className='col-12'>
										<Card className='rounded-2 border-light shadow-sm mb-4'>
											<CardHeader className='bg-light py-2'>
												<CardLabel icon='Home' iconColor='primary'>
													<CardTitle className='h6 mb-0'>
														Détails d'occupation
													</CardTitle>
												</CardLabel>
											</CardHeader>
											<CardBody>
												<div className='row g-3'>
													{/* Type d'occupation */}
													<FormGroup
														className='col-12 col-md-4'
														label="Type d'occupation">
														<Select
															name='typeOccupation'
															value={formik.values.typeOccupation}
															onChange={formik.handleChange}>
															<option value=''>Sélectionner</option>
															<option value='proprietaire'>
																Propriétaire
															</option>
															<option value='locataire'>
																Locataire
															</option>
														</Select>
													</FormGroup>

													{/* Interphone */}
													<FormGroup
														className='col-12 col-md-4'
														label="Numéro d'interphone">
														<Input
															name='interphone'
															value={formik.values.interphone}
															onChange={formik.handleChange}
															type='text'
															placeholder="Numéro d'interphone"
														/>
													</FormGroup>

													{/* Numéro d'appartement */}
													<FormGroup
														className='col-12 col-md-4'
														label="Numéro d'appartement">
														<Input
															name='numeroAppartement'
															value={formik.values.numeroAppartement}
															onChange={formik.handleChange}
															type='text'
															placeholder='Ex: A12, B3...'
														/>
													</FormGroup>

													{/* Code d'accès */}
													<FormGroup
														className='col-12 col-md-4'
														label="Code d'accès">
														<Input
															name='codeAcces'
															value={formik.values.codeAcces}
															onChange={formik.handleChange}
															type='text'
															placeholder="Code d'accès"
														/>
													</FormGroup>

													{/* Numéro du propriétaire (conditionnel) */}
													{formik.values.typeOccupation ===
														'locataire' && (
															<FormGroup
																className='col-12 col-md-8'
																label='Téléphone du propriétaire'>
																<Input
																	name='numeroProprietaire'
																	value={
																		formik.values.numeroProprietaire
																	}
																	onChange={formik.handleChange}
																	type='tel'
																	placeholder='01 23 45 67 89'
																/>
																{formik.touched.numeroProprietaire &&
																	formik.errors
																		.numeroProprietaire && (
																		<small className='text-danger'>
																			{
																				formik.errors
																					.numeroProprietaire
																			}
																		</small>
																	)}
																<small className='text-muted'>
																	Requis pour les locataires
																</small>
															</FormGroup>
														)}
												</div>
											</CardBody>
										</Card>
									</div>

									{/* Téléphones */}
									<div className='col-12 col-lg-6'>
										<Card className='rounded-2 border-light shadow-sm'>
											<CardHeader className='bg-light py-2'>
												<CardLabel icon='Phone' iconColor='primary'>
													<CardTitle className='h6 mb-0'>
														Téléphones
													</CardTitle>
												</CardLabel>
											</CardHeader>
											<CardBody>
												{formik.values.telephones.map((phone, idx) => (
													<div key={idx} className='mb-3 p-2 border rounded bg-white shadow-none'>
														<div className='d-flex align-items-center gap-2 mb-2'>

															{/* FREE STRING LABEL INPUT */}

															{/* PHONE NUMBER INPUT */}
															<div className='col-md-6'>
																<Input
																	value={phone}
																	onChange={(e) =>
																		handleArrayFieldChange('telephones', idx, e.target.value)
																	}
																	type='tel'
																	placeholder='+216 98 000 000'

																/>
															</div>
															<div style={{ width: '130px' }}>
																<Input
																	value={formik.values.telephones_labels?.[idx] || ''}
																	onChange={(e) =>
																		handleArrayFieldChange('telephones_labels', idx, e.target.value)
																	}
																	type='text'
																	placeholder='Libellé'

																/>
															</div>


															{/* DELETE BUTTON */}
															{formik.values.telephones.length > 1 && (
																<Button
																	color='outline-danger'
																	size='sm'
																	onClick={() => removeField('telephones', idx)}
																	icon='Delete'
																/>
															)}
														</div>

														{/* ERROR DISPLAY */}
														{formik.errors.telephones?.[idx] && (
															<small className='text-danger d-block' style={{ fontSize: '10px' }}>
																{formik.errors.telephones[idx]}
															</small>
														)}
													</div>
												))}
												<Button
													type='button'
													color='outline-primary'
													onClick={() => addField('telephones')}
													className='w-100 mt-2'
													icon='Add'
													size='sm'>
													Ajouter un téléphone
												</Button>
												{formik.touched.telephones &&
													formik.errors.telephones && (
														<small className='text-danger'>
															{formik.errors.telephones}
														</small>
													)}
											</CardBody>
										</Card>
									</div>

									{/* Emails */}
									<div className='col-12 col-lg-6'>
										<Card className='rounded-2 border-light shadow-sm'>
											<CardHeader className='bg-light py-2'>
												<CardLabel icon='Email' iconColor='info'>
													<CardTitle className='h6 mb-0'>
														Emails
													</CardTitle>
												</CardLabel>
											</CardHeader>
											<CardBody>
												{formik.values.emails.map((email, idx) => (
													<div
														key={idx}
														className='d-flex align-items-center mb-3'>
														<div className='flex-fill me-2'>
															<Input
																value={email}
																onChange={(e) =>
																	handleArrayFieldChange(
																		'emails',
																		idx,
																		e.target.value,
																	)
																}
																type='email'
																placeholder='exemple@email.com'
															/>
														</div>
														{formik.values.emails.length > 1 && (
															<Button
																color='outline-danger'
																size='sm'
																onClick={() =>
																	removeField('emails', idx)
																}
																icon='Delete'
															/>
														)}
													</div>
												))}
												<Button
													type='button'
													color='outline-info'
													onClick={() => addField('emails')}
													className='w-100 mt-2'
													icon='Add'
													size='sm'>
													Ajouter un email
												</Button>
												{formik.touched.emails && formik.errors.emails && (
													<small className='text-danger'>
														{formik.errors.emails}
													</small>
												)}
											</CardBody>
										</Card>
									</div>
									<div className='col-12 col-lg-6'>
										<Card className='rounded-2 border-light shadow-sm'>
											<CardHeader className='bg-light bg-opacity-25 py-3 d-flex justify-content-between align-items-center'>
												<CardLabel
													icon='LocalShipping'
													iconColor='primary'
													className='mb-0'>
													<CardTitle className='h6 mb-0'>
														Adresse(s) du chantier
													</CardTitle>
												</CardLabel>
												{/* {formik.values.type === 'societe' && ( */}
												<Button
													color='primary'
													size='sm'
													onClick={() =>
														addAddress('shippingAddresses')
													}
													icon='Add'>
													Ajouter
												</Button>
												{/* )} */}
											</CardHeader>
											<CardBody>
												{formik.values.shippingAddresses.map(
													(address, idx) => (
														<div
															key={`shipping-${idx}`}
															className='mb-4 p-3 border rounded'>
															<div className='row g-2'>
																{/* Formatted Address */}
																<FormGroup
																	className='col-12'
																	label='Adresse complète'>
																	<SearchAutoComplete
																		value={
																			address.formatted || ''
																		}
																		onChange={(e) =>
																			handleShippingSearch(
																				e,
																				idx,
																			)
																		}
																		options={
																			shippingSearchResults[
																			idx
																			] || []
																		}
																		setSelectItem={(item) =>
																			handleSelectShipping(
																				item,
																				idx,
																			)
																		}
																		placeholder='Commencez à taper une adresse...'
																	/>
																</FormGroup>

																{/* Street */}
																<FormGroup
																	className='col-12 col-sm-6'
																	label='Rue'>
																	<Input
																		value={address.street || ''}
																		onChange={(e) =>
																			handleAddressFieldChange(
																				'shippingAddresses',
																				idx,
																				'street',
																				e.target.value,
																			)
																		}
																		type='text'
																		placeholder='123 Rue Example'
																	/>
																</FormGroup>

																{/* Zip */}
																<FormGroup
																	className='col-12 col-sm-6'
																	label='Code postal'>
																	<Input
																		value={address.zip || ''}
																		onChange={(e) =>
																			handleAddressFieldChange(
																				'shippingAddresses',
																				idx,
																				'zip',
																				e.target.value,
																			)
																		}
																		type='text'
																		placeholder='75000'
																	/>
																</FormGroup>

																{/* City */}
																<FormGroup
																	className='col-12 col-sm-6'
																	label='Ville'>
																	<Input
																		value={address.city || ''}
																		onChange={(e) =>
																			handleAddressFieldChange(
																				'shippingAddresses',
																				idx,
																				'city',
																				e.target.value,
																			)
																		}
																		type='text'
																		placeholder='Paris'
																	/>
																</FormGroup>

																{/* State */}
																<FormGroup
																	className='col-12 col-sm-6'
																	label='Région/État'>
																	<Input
																		value={address.state || ''}
																		onChange={(e) =>
																			handleAddressFieldChange(
																				'shippingAddresses',
																				idx,
																				'state',
																				e.target.value,
																			)
																		}
																		type='text'
																		placeholder='Région ou état'
																	/>
																</FormGroup>

																{/* Country */}
																<FormGroup
																	className='col-12 col-sm-6'
																	label='Pays'>
																	<Input
																		value={
																			address.country || ''
																		}
																		onChange={(e) =>
																			handleAddressFieldChange(
																				'shippingAddresses',
																				idx,
																				'country',
																				e.target.value,
																			)
																		}
																		type='text'
																		placeholder='France'
																	/>
																</FormGroup>

																{/* Location Coordinates */}
																<FormGroup
																	className='col-12 col-md-6'
																	label='Longitude'>
																	<Input
																		value={
																			address.location
																				?.coordinates[0] ||
																			0
																		}
																		onChange={(e) =>
																			handleLocationChange(
																				'shippingAddresses',
																				idx,
																				0,
																				e.target.value,
																			)
																		}
																		type='number'
																		step='any'
																		min='-180'
																		max='180'
																		placeholder='Longitude (-180 à 180)'
																	/>
																	<small className='text-muted'>
																		Longitude: -180 à 180
																	</small>
																</FormGroup>

																<FormGroup
																	className='col-12 col-md-6'
																	label='Latitude'>
																	<Input
																		value={
																			address.location
																				?.coordinates[1] ||
																			0
																		}
																		onChange={(e) =>
																			handleLocationChange(
																				'shippingAddresses',
																				idx,
																				1,
																				e.target.value,
																			)
																		}
																		type='number'
																		step='any'
																		min='-90'
																		max='90'
																		placeholder='Latitude (-90 à 90)'
																	/>
																	<small className='text-muted'>
																		Latitude: -90 à 90
																	</small>
																</FormGroup>
																<FormGroup
																	className='col-12'
																	label='Lien Google Maps'>
																	<div className='input-group'>
																		<Input
																			value={address.map_url || ''}
																			onChange={(e) =>
																				handleAddressFieldChange(
																					'shippingAddresses',
																					idx,
																					'map_url',
																					e.target.value,
																				)
																			}
																			type='text'
																			placeholder='URL Google Maps'
																			readOnly
																			className='bg-light'
																		/>
																		{address.map_url && (
																			<Button
																				color='primary'
																				tag='a'
																				href={address.map_url}
																				target='_blank'
																				rel='noopener noreferrer'
																				icon='OpenInNew'>
																				Ouvrir
																			</Button>
																		)}
																	</div>
																	<small className='text-muted'>
																		Lien généré automatiquement à partir de l'adresse
																	</small>
																</FormGroup>
															</div>
															{formik.values.type === 'societe' &&
																formik.values.shippingAddresses
																	.length > 1 && (
																	<div className='d-flex justify-content-end mt-2'>
																		<Button
																			color='outline-danger'
																			size='sm'
																			onClick={() =>
																				removeAddress(
																					'shippingAddresses',
																					idx,
																				)
																			}
																			icon='Delete'>
																			Supprimer
																		</Button>
																	</div>
																)}
														</div>
													),
												)}
											</CardBody>
										</Card>
									</div>
									{/* Adresse de facturation */}
									<div className='col-12 col-lg-6'>
										<Card className='rounded-2 border-light shadow-sm'>
											<CardHeader className='bg-light bg-opacity-25 py-3 d-flex justify-content-between align-items-center'>
												<CardLabel
													icon='ReceiptLong'
													iconColor='primary'
													className='mb-0'>
													<CardTitle className='h6 mb-0'>
														Adresse(s) de facturation
													</CardTitle>
												</CardLabel>
												{/* {formik.values.type === 'societe' && ( */}
												<Button
													color='primary'
													size='sm'
													onClick={() =>
														addAddress('billingAddresses')
													}
													icon='Add'>
													Ajouter
												</Button>
												{/* )} */}
											</CardHeader>
											<CardBody>
												{formik.values.billingAddresses.map(
													(address, idx) => (
														<div
															key={`billing-${idx}`}
															className='mb-4 p-3 border rounded'>
															<div className='row g-2'>
																{/* Formatted Address */}
																<FormGroup
																	className='col-12'
																	label='Adresse complète'>
																	<SearchAutoComplete
																		value={
																			address.formatted || ''
																		}
																		onChange={(e) =>
																			handleBillingSearch(
																				e,
																				idx,
																			)
																		}
																		options={
																			billingSearchResults[
																			idx
																			] || []
																		}
																		setSelectItem={(item) =>
																			handleSelectBilling(
																				item,
																				idx,
																			)
																		}
																		placeholder='Commencez à taper une adresse...'
																	/>
																</FormGroup>

																{/* Street */}
																<FormGroup
																	className='col-12 col-sm-6'
																	label='Rue'>
																	<Input
																		value={address.street || ''}
																		onChange={(e) =>
																			handleAddressFieldChange(
																				'billingAddresses',
																				idx,
																				'street',
																				e.target.value,
																			)
																		}
																		type='text'
																		placeholder='123 Rue Example'
																	/>
																</FormGroup>

																{/* Zip */}
																<FormGroup
																	className='col-12 col-sm-6'
																	label='Code postal'>
																	<Input
																		value={address.zip || ''}
																		onChange={(e) =>
																			handleAddressFieldChange(
																				'billingAddresses',
																				idx,
																				'zip',
																				e.target.value,
																			)
																		}
																		type='text'
																		placeholder='75000'
																	/>
																</FormGroup>

																{/* City */}
																<FormGroup
																	className='col-12 col-sm-6'
																	label='Ville'>
																	<Input
																		value={address.city || ''}
																		onChange={(e) =>
																			handleAddressFieldChange(
																				'billingAddresses',
																				idx,
																				'city',
																				e.target.value,
																			)
																		}
																		type='text'
																		placeholder='Paris'
																	/>
																</FormGroup>

																{/* State */}
																<FormGroup
																	className='col-12 col-sm-6'
																	label='Région/État'>
																	<Input
																		value={address.state || ''}
																		onChange={(e) =>
																			handleAddressFieldChange(
																				'billingAddresses',
																				idx,
																				'state',
																				e.target.value,
																			)
																		}
																		type='text'
																		placeholder='Région ou état'
																	/>
																</FormGroup>

																{/* Country */}
																<FormGroup
																	className='col-12 col-sm-6'
																	label='Pays'>
																	<Input
																		value={
																			address.country || ''
																		}
																		onChange={(e) =>
																			handleAddressFieldChange(
																				'billingAddresses',
																				idx,
																				'country',
																				e.target.value,
																			)
																		}
																		type='text'
																		placeholder='France'
																	/>
																</FormGroup>

																{/* Location Coordinates */}
																<FormGroup
																	className='col-12 col-md-6'
																	label='Longitude'>
																	<Input
																		value={
																			address.location
																				?.coordinates[0] ||
																			0
																		}
																		onChange={(e) =>
																			handleLocationChange(
																				'billingAddresses',
																				idx,
																				0,
																				e.target.value,
																			)
																		}
																		type='number'
																		step='any'
																		min='-180'
																		max='180'
																		placeholder='Longitude (-180 à 180)'
																	/>
																	<small className='text-muted'>
																		Longitude: -180 à 180
																	</small>
																</FormGroup>

																<FormGroup
																	className='col-12 col-md-6'
																	label='Latitude'>
																	<Input
																		value={
																			address.location
																				?.coordinates[1] ||
																			0
																		}
																		onChange={(e) =>
																			handleLocationChange(
																				'billingAddresses',
																				idx,
																				1,
																				e.target.value,
																			)
																		}
																		type='number'
																		step='any'
																		min='-90'
																		max='90'
																		placeholder='Latitude (-90 à 90)'
																	/>
																	<small className='text-muted'>
																		Latitude: -90 à 90
																	</small>
																</FormGroup>
																{/* Map URL */}
																<FormGroup
																	className='col-12'
																	label='Lien Google Maps'>
																	<div className='input-group'>
																		<Input
																			value={address.map_url || ''}
																			onChange={(e) =>
																				handleAddressFieldChange(
																					'billingAddresses',
																					idx,
																					'map_url',
																					e.target.value,
																				)
																			}
																			type='text'
																			placeholder='URL Google Maps'
																			readOnly
																			className='bg-light'
																		/>
																		{address.map_url && (
																			<Button
																				color='primary'
																				tag='a'
																				href={address.map_url}
																				target='_blank'
																				rel='noopener noreferrer'
																				icon='OpenInNew'>
																				Ouvrir
																			</Button>
																		)}
																	</div>
																	<small className='text-muted'>
																		Lien généré automatiquement à partir de l'adresse
																	</small>
																</FormGroup>
															</div>
															{formik.values.type === 'societe' &&
																formik.values.billingAddresses
																	.length > 1 && (
																	<div className='d-flex justify-content-end mt-2'>
																		<Button
																			color='outline-danger'
																			size='sm'
																			onClick={() =>
																				removeAddress(
																					'billingAddresses',
																					idx,
																				)
																			}
																			icon='Delete'>
																			Supprimer
																		</Button>
																	</div>
																)}
														</div>
													),
												)}
											</CardBody>
										</Card>
									</div>

									{/* Adresse de livraison */}
								</div>
							</CardBody>

							<CardFooter className='d-flex justify-content-between align-items-center border-top bg-light bg-opacity-10'>
								<div className='d-flex align-items-center'>
									{spinner && <Spinner isGrow isSmall className='me-2' />}
									{!!success && (
										<StatusPopup
											icon='Verified'
											color='success'
											title='Super !'
											label={success}
											setOpen={setSuccess}
										/>
									)}
									{!!error && (
										<ErrorPopup
											icon='ErrorOutline'
											color='danger'
											title='Erreur'
											label={error}
											setOpen={setError}
										/>
									)}
								</div>
								<div className='d-flex gap-2'>
									<Button
										type='button'
										onClick={handleClose}
										color='secondary'
										outline
										disabled={spinner}>
										Annuler
									</Button>
									<Button type='submit' color='primary' disabled={spinner}>
										{spinner ? (
											<>
												<Spinner isSmall inButton />
												<span className='ms-2'>
													{isNewClient ? 'Création...' : 'Mise à jour...'}
												</span>
											</>
										) : isNewClient ? (
											'Créer le client'
										) : (
											'Mettre à jour'
										)}
									</Button>
								</div>
							</CardFooter>
						</Card>
					</Modal.Body>
				</form>
			</Modal>

			{/* External Popups */}
			{success && (
				<StatusPopup
					icon='Verified'
					color='success'
					title='Super!'
					label={success}
					setOpen={setSuccess}
				/>
			)}
			{!!error && <ErrorPopup error={error} setOpen={setError} />}
		</>
	);
};

export default ManageClient;